import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import { faChevronDown, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useState } from 'react'
import { type Control, useFieldArray } from 'react-hook-form'
import {
  attachmentDropdown,
  attachmentDropdownItem,
  attachmentDropdownPortal,
  attachmentDropdownTrigger,
  attachmentDropdownWrapper,
  attachmentItem,
  attachmentLabel,
  attachmentRemove,
  attachmentsList
} from '~/components/Form/Input/AttachmentInput.css'
import { openIconOpen } from '~/components/Form/Input/Dropdown.css'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { getIconFromMime } from '~/components/Global/Elements/File/FileType'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'

export type AttachmentField =
  | {
      fromTemplate: true
      id?: string
      object: {
        id: number
        uri: string
        filename: string | null
        size: number | null
        mime?: string | null
      }
    }
  | {
      fromTemplate: false
      id?: string
      object: {
        base64_content: string
        filename?: string | null
        size?: number | null
        mime?: string | null
      }
    }

type AttachmentsInputProps = InputInFormProps & {
  control?: Control<{
    attachments: AttachmentField[]
  }>
}

export const AttachmentsInput = ({
  control,
  watch
}: AttachmentsInputProps): ReactElement | null => {
  const [isOpen, setIsOpen] = useState(false)

  if (control === undefined || watch === undefined) {
    throw new Error('AttachmentsInput must live inside a Form component')
  }
  const { remove } = useFieldArray({
    control,
    name: 'attachments'
  })
  const attachments = watch('attachments')

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    dismiss,
    role
  ])

  if (!attachments || attachments.length === 0) {
    return null
  }

  if (attachments.length <= 2) {
    return (
      <div className={attachmentsList}>
        {attachments.map((attachment: any, index: number) => (
          <div key={attachment.id ?? index} className={attachmentItem}>
            <FontAwesomeIcon
              icon={getIconFromMime({ mime: attachment.object.mime })}
            />
            <span className={attachmentLabel}>
              {attachment.object.filename}
            </span>
            {!attachment.fromTemplate && (
              <button
                type="button"
                onClick={() => remove(index)}
                title="Supprimer la pièce jointe"
                className={attachmentRemove}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      <div
        className={clsx(attachmentDropdownWrapper, attachmentItem)}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className={attachmentDropdownTrigger}>
          <strong>+{attachments.length}</strong> pièces jointes
          <button type="button" className={attachmentDropdownTrigger}>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={clsx(isOpen ? openIconOpen : null)}
            />
          </button>
        </div>
      </div>
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={attachmentDropdownPortal}
            {...getFloatingProps()}
          >
            <div
              style={{ ...transitionStyles }}
              className={clsx(themeClass, attachmentDropdown)}
            >
              {attachments.map((attachment: any, index: number) => (
                <div key={attachment.id} className={attachmentDropdownItem}>
                  <FontAwesomeIcon
                    icon={getIconFromMime({ mime: attachment.object.mime })}
                  />
                  <span className={attachmentLabel}>
                    {attachment.object.filename}
                  </span>
                  {!attachment.fromTemplate && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      title="Supprimer la pièce jointe"
                      className={attachmentRemove}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}
